import { useSyncExternalStore } from 'react';
import { C, S, R, T, SERIF } from '../theme';
import { Icon } from '../icons';
import {
  getInstallPrompt,
  subscribeInstallPrompt,
  runInstallPrompt,
  isStandalone,
  isIOS,
} from '../pwa';

/**
 * Offers to install Premise, and renders nothing when there is nothing to offer.
 *
 * Three states, because the platforms genuinely differ:
 *  - Already installed: nothing. The offer would be noise.
 *  - Android / desktop Chrome / Edge: a real button. Chrome hands us a
 *    `beforeinstallprompt` event which pwa.ts stashes at boot, and we replay it
 *    on this tap. Subscribed through useSyncExternalStore because the event can
 *    land before or after this component mounts.
 *  - iOS: a coach card, because iOS has NO install prompt API. Safari only
 *    installs via Share, then Add to Home Screen, so the honest thing is to
 *    show the steps rather than a button that cannot work.
 *
 * COPY CONSTRAINT (carried from trail): the service worker caches the SHELL
 * only. The ledger, rooms and collections are live API calls it never caches.
 * So this says "opens instantly", never "works offline" or "works on a weak
 * signal", which would be a promise the app does not keep.
 */
export function InstallCard() {
  const prompt = useSyncExternalStore(subscribeInstallPrompt, getInstallPrompt, () => null);

  if (isStandalone()) return null;

  const ios = isIOS();
  if (!prompt && !ios) return null; // no offer to make on this browser

  return (
    <div
      style={{
        background: S.surface.card,
        border: `1px solid ${S.edge.hairline}`,
        borderRadius: R.lg,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          padding: '12px 16px',
          borderBottom: `1px solid ${S.edge.divider}`,
        }}
      >
        <span
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: S.surface.accent,
            color: C.accent2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          <Icon n="home" size={16} />
        </span>
        <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 650, color: S.text.primary }}>
          Install Premise
        </h3>
      </div>

      <div style={{ padding: '10px 16px 15px' }}>
        <div style={{ fontSize: T.body, color: S.text.secondary, lineHeight: 1.55 }}>
          Keep Premise on your home screen. It opens instantly, runs without the browser
          bars, and updates itself when there is a new version.
        </div>

        {prompt ? (
          <button
            onClick={() => void runInstallPrompt()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 13,
              background: C.accent,
              color: S.text.onAccent,
              border: 0,
              borderRadius: R.md,
              padding: '10px 16px',
              fontWeight: 600,
              fontSize: 13.5,
              cursor: 'pointer',
            }}
          >
            <Icon n="plus" size={15} color={S.text.onAccent} /> Install app
          </button>
        ) : (
          <ol
            style={{
              margin: '13px 0 0',
              padding: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 9,
            }}
          >
            {[
              ['Tap Share at the bottom of Safari', 'the square with an arrow pointing up'],
              ['Choose Add to Home Screen', 'scroll the list if you do not see it'],
              ['Tap Add', 'Premise appears with your other apps'],
            ].map(([step, hint], i) => (
              <li key={i} style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
                <span
                  style={{
                    fontFamily: 'ui-monospace, monospace',
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: C.accent2,
                    background: S.surface.accent,
                    borderRadius: 7,
                    width: 22,
                    height: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 'none',
                  }}
                >
                  {i + 1}
                </span>
                <span>
                  <span style={{ display: 'block', fontSize: T.body, color: S.text.primary }}>
                    {step}
                  </span>
                  <span style={{ display: 'block', fontSize: 11.8, color: S.text.muted, marginTop: 1 }}>
                    {hint}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}

        {!prompt && (
          <div
            style={{
              marginTop: 13,
              fontFamily: SERIF,
              fontSize: 13,
              color: S.text.muted,
              fontStyle: 'italic',
            }}
          >
            iPhone and iPad can only add an app this way. There is no button Safari lets us
            offer.
          </div>
        )}
      </div>
    </div>
  );
}
